import { DateRangePicker } from '@progress/kendo-react-dateinputs'
import bodybuilder from 'bodybuilder'
import { DropdownComponent, OnChangeEvent } from 'components/dropdown/component'
import { HeaderComponent } from 'components/header/component'
import { Loading } from 'components/loading'
import { addDays, format } from 'date-fns'
import {
  AggregationType,
  DateRangesAsDropdownOptions,
  FilterType,
  IndexName,
  LASTQUARTER,
  toFilter,
} from 'elasticsearch/constants'
import { ResponsePayload } from 'elasticsearch/types'
import { ElasticsearchComponent, ElasticsearchProps } from 'generic/elasticSearchComponent'
import * as GenericRedux from 'generic/genericRedux'
import React, { ComponentType } from 'react'
import { ConnectedComponentClass } from 'react-redux'
import { formatDateForES } from 'shared_server_client/dates'
import { Fields } from '../../shared/modals'
import { lastQuarter } from '../../elasticsearch'
import { PieChart, PieChartData } from '../charts/pie'
import * as Constants from './constants'
import { CHART_COLORS } from './constants'
import { RasaAnalyticsComponent } from './rasa-analytics-component'
import './styles.css'

const GROUPING_OPTIONS = [
  { key: '1', value: 'provider', description: 'Provider' },
  { key: '2', value: 'prompt_type', description: 'Prompt Type' },
  { key: '3', value: 'model', description: 'Model' },
]

const CUSTOM_DATE_RANGE_KEY = '7'
const ELASTICSEARCH_AGG_SIZE = 100
const REPORT_NAME = 'AI Usage'
const REPORT_TITLE = 'REPORTS'
const REPORT_SUBTITLE = 'AI Usage Report'
const REPORT_DESCRIPTION = 'Track AI API calls, token usage, and costs across your newsletter.'
const DATE_FORMAT = 'MMM dd, yyyy'
const COST_DECIMAL_PLACES = 6
const EXPAND_ICON = '▼'
const COLLAPSE_ICON = '▶'

interface DetailStats {
  promptType: string
  provider: string
  model: string
  totalCalls: number
  totalTokens: number
  promptTokens: number
  completionTokens: number
  averageTokensPerCall: number
  averageResponseTime: number
  totalCost: number
}

interface GroupedItem {
  key: string
  label: string
  totalCalls: number
  totalTokens: number
  totalCost: number
  children?: GroupedItem[]
  details?: DetailStats[]
  expanded?: boolean
}

interface DateGroupStats {
  date: string
  dateString: string
  totalCalls: number
  totalTokens: number
  totalCost: number
  groups: GroupedItem[]
  expanded?: boolean
}

interface AiUsageStats {
  dateGroups: DateGroupStats[]
  overallStats: {
    totalCalls: number
    totalTokens: number
    totalCost: number
    averageTokensPerCall: number
    averageResponseTime: number
  }
  primaryBreakdown: PieChartData[]
  rawDetails: { [date: string]: DetailStats[] }
}

interface AiUsageProps extends ElasticsearchProps<AiUsageStats> {
  dateRange: string
  timezone?: string
  useTimezoneForAnalytics?: number
  primaryGrouping: string
}

interface AiUsageTableState {
  isLoading: boolean
  loaded: boolean
  stats: AiUsageStats | null
}

const InitialAiUsageStats: AiUsageStats = {
  dateGroups: [],
  overallStats: {
    totalCalls: 0,
    totalTokens: 0,
    totalCost: 0,
    averageTokensPerCall: 0,
    averageResponseTime: 0,
  },
  primaryBreakdown: [],
  rawDetails: {},
}

export class AiUsageComponentClass extends RasaAnalyticsComponent<any, any> {
  constructor(props) {
    super(props)
    this.state = {
      ...this.state,
      selectedDateRange: DateRangesAsDropdownOptions.filter((x) => x.value ===
        this.getQuery(Constants.AnalyticsFilterKeys.SELECTED_DATE_RANGE, DateRangesAsDropdownOptions[2].value))[0],
      selectedPrimaryGrouping: GROUPING_OPTIONS[0],
    }
  }

  public componentDidMount() {
    super.componentDidMount()
    this.minCustomDateRange()
  }

  public render() {
    return (
      <div className="analytics-component">
        <HeaderComponent
          title={REPORT_TITLE}
          subTitle={REPORT_SUBTITLE}
          description={[REPORT_DESCRIPTION]}
        />
        <div className="dropdown-header">
          <div className="dropdown-inline">
            <DropdownComponent
              data={DateRangesAsDropdownOptions}
              selected={this.state.selectedDateRange.key}
              onChange={this.dateChanged}
            />
          </div>
          <div className="dropdown-inline">
            <DropdownComponent
              data={GROUPING_OPTIONS}
              selected={this.state.selectedPrimaryGrouping.key}
              onChange={this.primaryGroupingChanged}
            />
          </div>
        </div>
        <div className="date-range-picker">
          {this.state.selectedDateRange.key === CUSTOM_DATE_RANGE_KEY ?
            <DateRangePicker
              min={this.state.minCustomDateRange}
              max={new Date()}
              onChange={this.createCustomDate}
            /> : null}
        </div>
        {this.state.isFilterLoaded &&
        <AnalyticsAiUsageTable
          key={`${this.state.selectedDateRange.value}-${this.state.selectedPrimaryGrouping.value}`}
          dateRange={this.state.selectedDateRange.value}
          timezone={this.state.timezone}
          useTimezoneForAnalytics={this.state.useTimezoneForAnalytics}
          primaryGrouping={this.state.selectedPrimaryGrouping.value} />}
      </div>
    )
  }

  public dateChanged = (e: OnChangeEvent) => {
    if (e.selected.description.toLowerCase() === LASTQUARTER.toLowerCase()) {
      const quarter = lastQuarter()
      e.selected.value = `${quarter.startDate}|${quarter.endDate}`
    }
    this.setState({
      selectedDateRange: e.selected,
    })
  }

  public createCustomDate = (e) => {
    if (e.value.start === null || e.value.end === null) {
      return
    } else {
      const customRange = `${formatDateForES(e.value.start)}|${formatDateForES(addDays(e.value.end, 1))}`
      this.setState({
        selectedDateRange: {
          ...this.state.selectedDateRange,
          value: customRange,
        },
      })
    }
  }

  public primaryGroupingChanged = (e: OnChangeEvent) => {
    this.setState({
      selectedPrimaryGrouping: e.selected,
    })
  }
}

class AiUsageTableComponent extends ElasticsearchComponent<AiUsageStats, AiUsageProps, AiUsageTableState> {
  constructor(p: AiUsageProps) {
    super(p, IndexName.AI_USAGE)
    this.state = {
      loaded: false,
      isLoading: false,
      stats: null,
    }
    this.reportName = REPORT_NAME
  }

  public componentDidMount() {
    this.context.user.init().then(({activeCommunity}) => {
      this.communityId = activeCommunity.communityId
      this.search()
    })
  }

  public componentDidUpdate(oldProps: AiUsageProps) {
    if (this.props.dateRange !== oldProps.dateRange) {
      this.setState({
        isLoading: true,
        loaded: false,
      })
      this.search()
    }
  }

  protected searchError() {
    this.setState({
      isLoading: false,
      loaded: false,
    })
  }

  protected onDataReceived(results: any) {
    this.setState({
      isLoading: false,
      loaded: true,
      stats: results,
    })
  }

  private getFieldValue = (detail: DetailStats, field: string): string => {
    switch (field) {
      case 'provider': return detail.provider
      case 'prompt_type': return detail.promptType
      case 'model': return detail.model
      default: return 'unknown'
    }
  }

  private buildHierarchy = (details: DetailStats[], primaryField: string): GroupedItem[] => {
    const primaryGroups: { [key: string]: GroupedItem } = {}

    details.forEach((detail) => {
      const primaryKey = this.getFieldValue(detail, primaryField)

      if (!primaryGroups[primaryKey]) {
        primaryGroups[primaryKey] = {
          key: primaryKey,
          label: primaryKey,
          totalCalls: 0,
          totalTokens: 0,
          totalCost: 0,
          details: [],
          expanded: false,
        }
      }

      primaryGroups[primaryKey].totalCalls += detail.totalCalls
      primaryGroups[primaryKey].totalTokens += detail.totalTokens
      primaryGroups[primaryKey].totalCost += detail.totalCost
      primaryGroups[primaryKey].details.push(detail)
    })

    return Object.values(primaryGroups).sort((a, b) => b.totalCalls - a.totalCalls)
  }

  public parseResponse(payload: ResponsePayload): Promise<AiUsageStats> {
    const aggregations: any = payload.aggregations

    if (!aggregations || !aggregations.by_date || !aggregations.by_date.buckets) {
      return Promise.resolve(InitialAiUsageStats)
    }

    const dateBuckets = aggregations.by_date.buckets
    const rawDetails: { [date: string]: DetailStats[] } = {}
    const primaryTotals: { [key: string]: number } = {}
    const primaryCosts: { [key: string]: number } = {}

    let overallTotalCalls = 0
    let overallTotalTokens = 0
    let overallTotalCost = 0
    let overallTotalResponseTime = 0

    dateBuckets.forEach((dateBucket: any) => {
      const date = dateBucket.key_as_string || dateBucket.key
      const details: DetailStats[] = []

      if (dateBucket.by_prompt_type && dateBucket.by_prompt_type.buckets) {
        dateBucket.by_prompt_type.buckets.forEach((promptTypeBucket: any) => {
          const promptType = promptTypeBucket.key || 'unknown'

          if (promptTypeBucket.by_provider && promptTypeBucket.by_provider.buckets) {
            promptTypeBucket.by_provider.buckets.forEach((providerBucket: any) => {
              const provider = providerBucket.key

              if (providerBucket.by_model && providerBucket.by_model.buckets) {
                providerBucket.by_model.buckets.forEach((modelBucket: any) => {
                  const model = modelBucket.key
                  const totalCalls = modelBucket.doc_count || 0
                  const totalTokens = modelBucket.total_tokens && modelBucket.total_tokens.value ? modelBucket.total_tokens.value : 0
                  const promptTokens = modelBucket.prompt_tokens && modelBucket.prompt_tokens.value ? modelBucket.prompt_tokens.value : 0
                  const completionTokens = modelBucket.completion_tokens && modelBucket.completion_tokens.value ? modelBucket.completion_tokens.value : 0
                  const totalCost = modelBucket.total_cost && modelBucket.total_cost.value ? modelBucket.total_cost.value : 0
                  const averageResponseTime = modelBucket.avg_response_time && modelBucket.avg_response_time.value ? Math.round(modelBucket.avg_response_time.value) : 0
                  const averageTokensPerCall = totalCalls > 0 ? Math.round(totalTokens / totalCalls) : 0

                  const detail: DetailStats = {
                    promptType,
                    provider,
                    model,
                    totalCalls,
                    totalTokens,
                    promptTokens,
                    completionTokens,
                    averageTokensPerCall,
                    averageResponseTime,
                    totalCost,
                  }

                  details.push(detail)

                  overallTotalCalls += totalCalls
                  overallTotalTokens += totalTokens
                  overallTotalCost += totalCost
                  overallTotalResponseTime += (averageResponseTime * totalCalls)

                  const primaryKey = this.getFieldValue(detail, this.props.primaryGrouping)
                  primaryTotals[primaryKey] = (primaryTotals[primaryKey] || 0) + totalCalls
                  primaryCosts[primaryKey] = (primaryCosts[primaryKey] || 0) + totalCost
                })
              }
            })
          }
        })
      }

      rawDetails[date] = details
    })

    const dateGroups: DateGroupStats[] = Object.entries(rawDetails).map(([date, details]) => {
      const groups = this.buildHierarchy(details, this.props.primaryGrouping)
      const dateTotalCalls = details.reduce((sum, d) => sum + d.totalCalls, 0)
      const dateTotalTokens = details.reduce((sum, d) => sum + d.totalTokens, 0)
      const dateTotalCost = details.reduce((sum, d) => sum + d.totalCost, 0)

      const dateObj = new Date(date + 'T00:00:00')
      const dateString = dateObj && !isNaN(dateObj.getTime())
        ? format(dateObj, DATE_FORMAT)
        : date

      return {
        date,
        dateString,
        totalCalls: dateTotalCalls,
        totalTokens: dateTotalTokens,
        totalCost: dateTotalCost,
        groups,
        expanded: false,
      }
    }).sort((a, b) => {
      const dateA = new Date(a.date + 'T00:00:00')
      const dateB = new Date(b.date + 'T00:00:00')
      return dateB.getTime() - dateA.getTime()
    })

    const primaryBreakdown: PieChartData[] = Object.entries(primaryTotals)
      .sort((a, b) => b[1] - a[1])
      .map(([key, count], index) => ({
        category: key,
        totalRecords: count,
        totalCost: primaryCosts[key] || 0,
        color: CHART_COLORS[index % CHART_COLORS.length],
      }))

    const overallAverageTokensPerCall = overallTotalCalls > 0 ? Math.round(overallTotalTokens / overallTotalCalls) : 0
    const overallAverageResponseTime = overallTotalCalls > 0 ? Math.round(overallTotalResponseTime / overallTotalCalls) : 0

    return Promise.resolve({
      dateGroups,
      overallStats: {
        totalCalls: overallTotalCalls,
        totalTokens: overallTotalTokens,
        totalCost: overallTotalCost,
        averageTokensPerCall: overallAverageTokensPerCall,
        averageResponseTime: overallAverageResponseTime,
      },
      primaryBreakdown,
      rawDetails,
    })
  }

  public searchPayload(): any {
    this.setState({
      isLoading: true,
    })

    const query = this.generateSearchQuery()
      .aggregation(
        AggregationType.date_histogram,
        'request_date',
        {
          field: 'request_date',
          interval: 'day',
          format: 'yyyy-MM-dd',
        },
        'by_date',
        (dateAgg) => dateAgg
          .aggregation(
            AggregationType.terms,
            'prompt_type.keyword',
            {
              field: 'prompt_type.keyword',
              size: ELASTICSEARCH_AGG_SIZE,
            },
            'by_prompt_type',
            (promptTypeAgg) => promptTypeAgg
              .aggregation(
                AggregationType.terms,
                'provider.keyword',
                {
                  field: 'provider.keyword',
                  size: ELASTICSEARCH_AGG_SIZE,
                },
                'by_provider',
                (providerAgg) => providerAgg
                  .aggregation(
                    AggregationType.terms,
                    'model.keyword',
                    {
                      field: 'model.keyword',
                      size: ELASTICSEARCH_AGG_SIZE,
                    },
                    'by_model',
                    (modelAgg) => modelAgg
                      .aggregation('sum', 'total_tokens', {field: 'total_tokens'}, 'total_tokens')
                      .aggregation('sum', 'prompt_tokens', {field: 'prompt_tokens'}, 'prompt_tokens')
                      .aggregation('sum', 'completion_tokens', {field: 'completion_tokens'}, 'completion_tokens')
                      .aggregation('sum', 'cost', {field: 'cost'}, 'total_cost')
                      .aggregation('avg', 'response_time', {field: 'response_time'}, 'avg_response_time')
                  )
              )
          )
      )
      .build()

    return query
  }

  private generateSearchQuery = (): bodybuilder.Bodybuilder => {
    return bodybuilder()
      .size(0)
      .filter(
        FilterType.range,
        'request_date',
        toFilter(this.props.dateRange, this.props.timezone, this.props.useTimezoneForAnalytics)
      )
  }

  private toggleDateRow = (date: string) => {
    const newStats = {...this.state.stats}
    const dateGroup = newStats.dateGroups.find(dg => dg.date === date)
    if (dateGroup) {
      dateGroup.expanded = !dateGroup.expanded
      this.setState({stats: newStats})
    }
  }

  private toggleGroupRow = (date: string, groupKey: string) => {
    const newStats = {...this.state.stats}
    const dateGroup = newStats.dateGroups.find(dg => dg.date === date)
    if (dateGroup) {
      const group = dateGroup.groups.find(g => g.key === groupKey)
      if (group) {
        group.expanded = !group.expanded
      }
      this.setState({stats: newStats})
    }
  }

  private getOtherFieldsLabel = (detail: DetailStats): string => {
    const fields = []
    if (this.props.primaryGrouping !== 'provider') {
      fields.push(detail.provider)
    }
    if (this.props.primaryGrouping !== 'prompt_type') {
      fields.push(detail.promptType)
    }
    if (this.props.primaryGrouping !== 'model') {
      fields.push(detail.model)
    }
    return fields.join(' / ')
  }

  private renderPrimaryGroup = (date: string, group: GroupedItem, index: number) => (
    <React.Fragment key={index}>
      <tr
        onClick={() => this.toggleGroupRow(date, group.key)}
        className="ai-usage-group-row"
      >
        <td className="ai-usage-cell-indent-20">
          <span className="ai-usage-expand-icon">{group.expanded ? EXPAND_ICON : COLLAPSE_ICON}</span>
          <strong>{group.label}</strong>
        </td>
        <td>{group.totalCalls.toLocaleString()}</td>
        <td>{group.totalTokens.toLocaleString()}</td>
        <td>{this.formatCost(group.totalCost)}</td>
        <td colSpan={4}></td>
      </tr>
      {group.expanded && group.details && group.details.map((detail, idx) =>
        <tr key={idx} className="ai-usage-detail-row">
          <td className="ai-usage-cell-indent-40">{this.getOtherFieldsLabel(detail)}</td>
          <td>{detail.totalCalls.toLocaleString()}</td>
          <td>{detail.totalTokens.toLocaleString()}</td>
          <td>{detail.promptTokens.toLocaleString()}</td>
          <td>{detail.completionTokens.toLocaleString()}</td>
          <td>{detail.averageTokensPerCall.toLocaleString()}</td>
          <td>{detail.averageResponseTime.toLocaleString()}</td>
          <td>{this.formatCost(detail.totalCost)}</td>
        </tr>
      )}
    </React.Fragment>
  )

  private getPrimaryGroupingLabel = (): string => {
    const option = GROUPING_OPTIONS.find(opt => opt.value === this.props.primaryGrouping)
    return option ? option.description : 'Primary'
  }

  private formatCost = (cost: number): string => {
    return `$${cost.toFixed(COST_DECIMAL_PLACES)}`
  }

  public render = () => (
    <div>
      <div className="analytics ai-usage-compact">
        {this.state.isLoading ? (
          <div className="ai-usage-loading">
            <Loading size="64"/>
          </div>
        ) : this.state.loaded && this.state.stats ? (
          <div>
            <div className="t-rows-wrapper t-row--dark statistics-bar">
              <div className="ai-usage-stats-container">
                <div className="ai-usage-stat-item">
                  <span className="ai-usage-stat-label">Total Calls:</span>
                  <span className="ai-usage-stat-value">{this.state.stats.overallStats.totalCalls.toLocaleString()}</span>
                </div>
                <div className="ai-usage-stat-item">
                  <span className="ai-usage-stat-label">Total Tokens:</span>
                  <span className="ai-usage-stat-value">{this.state.stats.overallStats.totalTokens.toLocaleString()}</span>
                </div>
                <div className="ai-usage-stat-item">
                  <span className="ai-usage-stat-label">Avg Tokens/Call:</span>
                  <span className="ai-usage-stat-value">{this.state.stats.overallStats.averageTokensPerCall.toLocaleString()}</span>
                </div>
                <div className="ai-usage-stat-item">
                  <span className="ai-usage-stat-label">Avg Response:</span>
                  <span className="ai-usage-stat-value">{this.state.stats.overallStats.averageResponseTime.toLocaleString()}ms</span>
                </div>
                <div className="ai-usage-stat-item">
                  <span className="ai-usage-stat-label">Total Cost:</span>
                  <span className="ai-usage-stat-value-cost">{this.formatCost(this.state.stats.overallStats.totalCost)}</span>
                </div>
              </div>
            </div>

            <div className="breakdown-section">
              <h3 className="t-main-heading">{this.getPrimaryGroupingLabel()} Breakdown</h3>
              {this.state.stats.primaryBreakdown.length > 0 ? (
                <PieChart
                  field="totalRecords"
                  gridLabel={this.getPrimaryGroupingLabel()}
                  totalCount={this.state.stats.overallStats.totalCalls}
                  data={this.state.stats.primaryBreakdown}
                  enableTooltip={false}
                  showDataGrid={true}
                  additionalColumns={[
                    {
                      field: 'totalCost',
                      header: 'COST',
                      formatter: (value) => `$${value.toFixed(COST_DECIMAL_PLACES)}`,
                    },
                  ]}
                />
              ) : <p>No data available</p>}
            </div>

            <div className="t-rows-wrapper t-row--dark daily-details-section">
              <h2 className="t-main-heading">Daily Usage Details</h2>
              <div className="t-table-wrapper">
                <table className="t-table">
                  <thead>
                    <tr>
                      <th>Date / Grouping</th>
                      <th>Calls</th>
                      <th>Tokens</th>
                      <th>Cost (USD)</th>
                      <th>Prompt</th>
                      <th>Completion</th>
                      <th>Avg Tokens</th>
                      <th>Avg Response (ms)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {this.state.stats.dateGroups.map((dateGroup) => (
                      <React.Fragment key={dateGroup.date}>
                        <tr
                          onClick={() => this.toggleDateRow(dateGroup.date)}
                          className={dateGroup.expanded ? 'ai-usage-date-row expanded' : 'ai-usage-date-row'}
                        >
                          <td>
                            <span className="ai-usage-expand-icon">{dateGroup.expanded ? EXPAND_ICON : COLLAPSE_ICON}</span>
                            {dateGroup.dateString}
                          </td>
                          <td>{dateGroup.totalCalls.toLocaleString()}</td>
                          <td>{dateGroup.totalTokens.toLocaleString()}</td>
                          <td>{this.formatCost(dateGroup.totalCost)}</td>
                          <td colSpan={4}></td>
                        </tr>
                        {dateGroup.expanded && dateGroup.groups.map((group, idx) =>
                          this.renderPrimaryGroup(dateGroup.date, group, idx)
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        ) : (
          <div>
            <p className="no-data-tag">
              {Constants.NO_DATA_COPY}
            </p>
          </div>
        )}
      </div>
    </div>
  )
}

export const AnalyticsAiUsageTable: ConnectedComponentClass<ComponentType<AiUsageTableComponent>, Fields> = GenericRedux.registerNewComponent(
  AiUsageTableComponent, 'ai_usage', {})

export const AiUsageComponent = GenericRedux.registerNewComponent(
  AiUsageComponentClass,
  'analytics_ai_usage',
  {}
)
